import { attemptGetSettings, attemptGetUserSettings } from '../appActions';

import { ApiError } from '@pdcfrontendui/staffplan';
import { LocalStorageKey } from '../util/LocalStorageKey';
import { LogonResponse } from '@pdcplan/shared/api/Login';
import { ThunkDispatch } from '../types';
import { UserLoginTypeEnum } from '@pdcplan/shared/enums';
import { attemptLoadTeams } from '../ChooseTeam/ChooseTeamAction';
import { currentLanguage } from '../currentLanguage';
import { enablePushNotifications } from '../api/PushNotification';
import getApi from '../getApi';
import { localStorageRemove } from '../util/localStorage';
import type { GlobalSettings } from '@pdcplan/shared/api/Common';
import type { Store } from '../rootReducer';
import {
  initiateOpenIDAuthFlow,
  OpenIDConnectConfig,
} from '@pdcplan/shared/utils/OpenID';
import { OpenIdError } from '@pdcplan/shared/error';
import { noop } from '@pdcfrontendui/utils';

export enum LoginEvents {
  LOGIN_REQUEST = 'LOGIN_REQUEST',
  LOGIN_SUCCESS = 'LOGIN_SUCCESS',
  LOGIN_FAILURE = 'LOGIN_FAILURE',
  LOGOUT = 'LOGOUT',
}

export type LoginAction =
  | {
      type: LoginEvents.LOGOUT;
      // Reused from existing state, so that settings do not have to be fetched again
      globalSettings: GlobalSettings;
      activeScope: number;
    }
  | {
      type: LoginEvents.LOGIN_REQUEST;
    }
  | {
      type: LoginEvents.LOGIN_SUCCESS;
      token: string;
      userType: UserLoginTypeEnum;
      userId: number;
      fullName: string;
    }
  | {
      type: LoginEvents.LOGIN_FAILURE;
      errorMessage: string;
    };

export const loginRequest = (): LoginAction => ({
  type: LoginEvents.LOGIN_REQUEST,
});

export const loginSuccess = (
  token: string,
  userType: UserLoginTypeEnum,
  userId: number,
  fullName: string
): LoginAction => ({
  type: LoginEvents.LOGIN_SUCCESS,
  token,
  userId,
  userType,
  fullName,
});

export const loginFailure = (errorMessage: string): LoginAction => ({
  type: LoginEvents.LOGIN_FAILURE,
  errorMessage,
});

function loginSuccessHandler(dispatch: ThunkDispatch, json: LogonResponse) {
  void dispatch(attemptLoadTeams())
    .then(() => {
      void dispatch(attemptGetUserSettings());
      void dispatch(attemptGetSettings());
      enablePushNotifications(dispatch);
      dispatch(
        loginSuccess(
          json.token,
          json.userKey.UserType,
          json.userKey.UserId,
          json.name
        )
      );
    })
    .catch((err: unknown) => {
      if (err instanceof ApiError) {
        dispatch(loginFailure(err.message));
      }
    });
}

function loginFailureHandler(dispatch: ThunkDispatch, error: Error) {
  if (
    error instanceof ApiError &&
    error.response?.exception === 'noAccessToTeamPlan'
  ) {
    dispatch(loginFailure(currentLanguage.NoAccessToTeamPlan));
  } else {
    dispatch(loginFailure(error.message));
  }
}

export function attemptLogin(username: string, password: string) {
  return (dispatch: ThunkDispatch) => {
    dispatch(loginRequest());
    return getApi()
      .login(username.toLowerCase(), password)
      .then((json) => {
        loginSuccessHandler(dispatch, json);
      })
      .catch((err: unknown) => {
        loginFailureHandler(
          dispatch,
          err instanceof Error ? err : new Error('Unknown error')
        );
      });
  };
}

export function attemptOpenIdLogin(settings: OpenIDConnectConfig) {
  return async (dispatch: ThunkDispatch) => {
    dispatch(loginRequest());
    try {
      const code = await initiateOpenIDAuthFlow(settings, 'planner', false);
      const json = await getApi().loginOpenId(code, settings.redirectUri);
      loginSuccessHandler(dispatch, json);
    } catch (error) {
      if (error instanceof OpenIdError && error.type === 'openWindowFailed') {
        getApi().log(error).catch(noop);
      }
      loginFailureHandler(
        dispatch,
        error instanceof OpenIdError && error.userOnlyMessage
          ? new Error(error.userOnlyMessage)
          : new Error('Unknown error')
      );
    }
  };
}

export const logoutAction = (
  globalSettings: GlobalSettings,
  activeScope: number
): LoginAction => ({
  type: LoginEvents.LOGOUT,
  globalSettings,
  activeScope,
});

export function logout(shouldCallApi = true) {
  return (dispatch: ThunkDispatch, getState: () => Store) => {
    dispatch(
      logoutAction(
        getState().appReducer.globalSettings,
        getState().appReducer.settings.activeScope
      )
    );
    if (shouldCallApi) {
      // JWT is removed immediately after request is sent
      return getApi()
        .logout()
        .catch(() => {
          // ignore
        });
    } else {
      localStorageRemove(LocalStorageKey.token);
    }
  };
}
